// /account — your own identity and security: who you are, your device sessions
// (revoke any but the current one), and two-factor enrollment. All against /me.

import { useState } from "react";
import { toast } from "sonner";
import { IconDeviceLaptop, IconPencil, IconShieldCheck, IconShieldPlus } from "@tabler/icons-react";
import {
  errorText,
  meFull,
  revokeSession,
  totpConfirm,
  totpDisable,
  totpEnroll,
  updateDisplayName,
  type Session,
  type TotpEnrollment,
} from "../api";
import { useApi, useSetUser } from "../session";
import { useAsyncData } from "../lib/use-async";
import { fmtDateTime } from "../lib/format";
import { initials } from "../app/initials";
import { PageHeader } from "@/components/page-header";
import { QrCode } from "@/components/qr-code";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function AccountPage() {
  const api = useApi();
  const { data, error, loading, reload } = useAsyncData(() => meFull(api));

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader eyebrow="Your account" title="Account" />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load your account: {error}</AlertDescription>
        </Alert>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 p-4 text-muted-foreground">
          <Spinner className="size-4" />
          <span className="font-mono text-xs tracking-wider uppercase">Loading…</span>
        </div>
      )}

      {data && (
        <>
          <Card>
            <CardContent className="flex items-center gap-4">
              <Avatar className="size-12">
                <AvatarFallback>{initials(data.user.display_name)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-2">
                <span className="text-lg font-semibold">{data.user.display_name}</span>
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="secondary" className="font-mono">
                    {data.user.server_role}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">{data.user.id}</span>
                  <span className="text-xs text-muted-foreground">
                    signed in with {data.identities.join(", ") || "—"}
                  </span>
                </div>
              </div>
              <div className="ml-auto self-start">
                <EditNameDialog current={data.user.display_name} onChange={reload} />
              </div>
            </CardContent>
          </Card>

          <SessionsCard sessions={data.sessions} onChange={reload} />
          <TotpCard enrolled={data.totp_enrolled} onChange={reload} />
        </>
      )}
    </div>
  );
}

function EditNameDialog({ current, onChange }: { current: string; onChange: () => void }) {
  const api = useApi();
  const setUser = useSetUser();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const { user } = await updateDisplayName(api, name.trim());
      setUser(user); // reflect in the shell (avatar + name) without a reload
      toast.success("Display name updated");
      setOpen(false);
      onChange();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setName(current);
        setError(null);
      }}
    >
      <Button variant="outline" size="sm" render={<DialogTrigger />}>
        <IconPencil data-icon="inline-start" />
        Edit name
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit display name</DialogTitle>
          <DialogDescription>
            This is how you appear to others. Your login email doesn't change.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="edit-name">Display name</FieldLabel>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </Field>
          </FieldGroup>
        </form>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            disabled={busy || name.trim() === "" || name.trim() === current}
            onClick={() => void save()}
          >
            {busy && <Spinner />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionsCard({ sessions, onChange }: { sessions: Session[]; onChange: () => void }) {
  const api = useApi();

  async function revoke(s: Session) {
    try {
      await revokeSession(api, s.id);
      toast.success("Session revoked", { description: s.device_name });
      onChange();
    } catch (e) {
      toast.error("Couldn't revoke session", { description: errorText(e) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IconDeviceLaptop className="size-4" /> Devices & sessions
        </CardTitle>
        <CardDescription>Where you're signed in. Revoke anything you don't recognize.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {s.device_name}
                      {s.current && (
                        <Badge variant="secondary" className="font-mono text-[0.625rem]">
                          this device
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDateTime(s.last_seen_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDateTime(s.expires_at)}</TableCell>
                  <TableCell className="text-right">
                    {!s.current && (
                      <ConfirmDialog
                        trigger={
                          <Button variant="ghost" size="sm">
                            Revoke
                          </Button>
                        }
                        title="Revoke this session?"
                        description={`"${s.device_name}" will be signed out immediately.`}
                        confirmLabel="Revoke"
                        destructive
                        onConfirm={() => revoke(s)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function TotpCard({ enrolled, onChange }: { enrolled: boolean; onChange: () => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {enrolled ? <IconShieldCheck className="size-4 text-emerald-500" /> : <IconShieldPlus className="size-4" />}
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          {enrolled
            ? "Two-factor is on. You'll be asked for a code when you sign in."
            : "Add a time-based one-time code (TOTP) from an authenticator app for a second factor at login."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {enrolled ? (
          <DisableTotpDialog onDone={onChange} />
        ) : (
          <EnrollTotpDialog onDone={onChange} />
        )}
      </CardContent>
    </Card>
  );
}

function EnrollTotpDialog({ onDone }: { onDone: () => void }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setOpen(true);
    setEnrollment(null);
    setCode("");
    setError(null);
    try {
      setEnrollment(await totpEnroll(api));
    } catch (e) {
      setError(errorText(e));
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await totpConfirm(api, code);
      toast.success("Two-factor is on");
      setOpen(false);
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button onClick={() => void begin()}>
        <IconShieldPlus data-icon="inline-start" />
        Enable two-factor
      </Button>
      {/* Cap the dialog at the viewport and scroll the body — not the whole
          dialog — so the ~10rem QR can't push a vertically-centered dialog past
          a short/zoomed viewport. The header and the footer actions stay pinned
          (grid rows auto / 1fr / auto) while the QR + secret + code region
          scrolls, keeping "Verify & enable" and "Cancel" reachable at any
          height. */}
      <DialogContent className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>Set up two-factor</DialogTitle>
          <DialogDescription>
            Scan the QR code with your authenticator app (or add the secret manually), then enter
            the 6-digit code it shows.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          {!enrollment && !error && (
            <div className="flex items-center gap-2 py-4 text-muted-foreground">
              <Spinner className="size-4" /> <span className="font-mono text-xs">preparing…</span>
            </div>
          )}
          {enrollment && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2">
                <div className="rounded-md border bg-white p-3">
                  <QrCode
                    value={enrollment.otpauth_url}
                    title="Scan to add this account to your authenticator app"
                    className="size-40"
                  />
                </div>
                <span className="text-center text-xs text-muted-foreground">
                  Scan with your authenticator app, or enter the secret below manually.
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                  Secret
                </span>
                <code className="rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all select-all">
                  {enrollment.secret}
                </code>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void confirm();
                }}
              >
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="totp-enroll-code">6-digit code</FieldLabel>
                    <Input
                      id="totp-enroll-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </Field>
                </FieldGroup>
              </form>
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={!enrollment || busy || code === ""} onClick={() => void confirm()}>
            {busy && <Spinner />}
            Verify & enable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisableTotpDialog({ onDone }: { onDone: () => void }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function disable() {
    setBusy(true);
    setError(null);
    try {
      await totpDisable(api, code);
      toast.success("Two-factor disabled");
      setOpen(false);
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setCode("");
        setError(null);
      }}
    >
      <Button variant="destructive" render={<DialogTrigger />}>
        Disable two-factor
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disable two-factor?</DialogTitle>
          <DialogDescription>
            Enter a current code to confirm. Your account will no longer require a second factor.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void disable();
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="totp-disable-code">6-digit code</FieldLabel>
              <Input
                id="totp-disable-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
          </FieldGroup>
        </form>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button variant="destructive" disabled={busy || code === ""} onClick={() => void disable()}>
            {busy && <Spinner />}
            Disable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
